import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import PreLoader from "../../../core/common/preLoader";
import CopyRightInfo from "../../../core/common/copyRightInfo";
import useFetch from "../../../hooks/useFetch";
import { API } from "../../../common/Constants";
import { getAPIHeaders, getUserProfile } from "../../../common/Utils";
import CreateTicketModal from "./createTicketModal";
import { Profile } from "../../../core/data/interface";

// Define Ticket interface based on API response
interface TicketFile {
  id: number;
  fileName: string;
  fileUrl: string;
  fileType: string;
}

interface TicketConversation {
  id: number;
  message: string;
  createdBy: string;
  createdOn: string;
  isInternal?: boolean;
}

interface Ticket {
  id: number;
  accountId: number;
  title: string;
  subject: string;
  description: string;
  priority: string;
  status: string;
  username: string;
  createdOn: string;
  createdBy: string;
  updatedOn: string;
  updatedBy: string;
  userProfileImageUrl: string;
  ticketConversations: TicketConversation[];
  files: TicketFile[];
}

const Support = () => {
  const [userProfile, setUserProfile] = useState<Profile>();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  
  // API call setup
  const { callFetch, isLoading } = useFetch();

  const fetchUserData = useCallback(async () => {
    try {
      const response = await callFetch(
        API.PROFILE,
        'GET',
        getAPIHeaders(),
        null,
        null
      );
      if (response) {
        setUserProfile(response);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, []);

  const fetchSupportTickets = useCallback(async () => {
    setError(null);
    try {
      const response = await callFetch(
        API.GET_SUPPORT_TICKETS,
        'GET',
        getAPIHeaders(),
        null,
        null
      );
      if (response) {
        setTickets(response);
      }
    } catch (error) {
      console.error('Error fetching support tickets:', error);
      setError('Failed to load support tickets');
    }
  }, []);

  // Fetch user profile and tickets data
  useEffect(() => {
    let userProfile = getUserProfile();
    if(!userProfile){
      fetchUserData();
    } else {
      setUserProfile(userProfile);
    }    
    fetchSupportTickets();
  }, []);

  const handleTicketCreated = () => {
    setShowCreateTicket(false);
    fetchSupportTickets(); // Refresh tickets list
  };

  // Helper functions for ticket status and priority
  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { class: string; text: string } } = {
      'OPEN': { class: 'bg-success', text: 'Open' },
      'IN_PROGRESS': { class: 'bg-warning', text: 'In Progress' },
      'RESOLVED': { class: 'bg-success', text: 'Resolved' },
      'CLOSED': { class: 'bg-secondary', text: 'Closed' },
      'PENDING': { class: 'bg-warning', text: 'Pending' }
    };
    return statusMap[status] || { class: 'bg-secondary', text: status };
  };

  const getPriorityBadge = (priority: string) => {
    const priorityMap: { [key: string]: { class: string; text: string } } = {
      'LOW': { class: 'bg-success', text: 'Low' },
      'MEDIUM': { class: 'bg-warning', text: 'Medium' },
      'HIGH': { class: 'bg-danger', text: 'High' },
      'URGENT': { class: 'bg-dark', text: 'Urgent' }
    };
    return priorityMap[priority] || { class: 'bg-secondary', text: priority };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading state
  if (isLoading) {
    return (
      <>
        <PreLoader showLoader={isLoading} />
        <div className="page-wrapper">
          <div className="content">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <div className="text-center">
                <p className="text-muted">Loading support information...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Support</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.index}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Pages</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Support
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          <div className="row">
            {/* Left Side - Institution Details */}
            <div className="col-lg-4 col-md-12 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="ti ti-building me-2"></i>
                    Institution Details
                  </h5>
                </div>
                <div className="card-body">
                  {userProfile?.profileSettings ? (
                    <>
                      {/* Institution Logo and Name */}
                      <div className="text-center mb-4">
                        {userProfile.profileSettings.institution_logo && (
                          <ImageWithBasePath
                            src={userProfile.profileSettings.institution_logo}
                            alt={userProfile.profileSettings.institution_name || 'Institution Logo'}
                            className="img-fluid rounded mb-3"
                            style={{ maxHeight: '100px', maxWidth: '200px', objectFit: 'contain' }}
                          />
                        )}
                        <h4 className="mb-1">{userProfile.profileSettings.institution_name || 'Institution Name'}</h4>
                      </div>

                      {/* Contact Information */}
                      <div className="contact-info">
                        <h6 className="mb-3">
                          <i className="ti ti-phone me-2 text-primary"></i>
                          Contact Information
                        </h6>
                        
                        <div className="contact-item mb-3">
                          <div className="d-flex align-items-center">
                            <i className="ti ti-phone text-success me-3"></i>
                            <div>
                              <small className="text-muted">Phone</small>
                              <div className="fw-medium">{userProfile.profileSettings.phone_number || 'Not provided'}</div>
                            </div>
                          </div>
                        </div>

                        <div className="contact-item mb-3">
                          <div className="d-flex align-items-center">
                            <i className="ti ti-brand-whatsapp text-success me-3"></i>
                            <div>
                              <small className="text-muted">WhatsApp</small>
                              <div className="fw-medium">{userProfile.profileSettings.whatsapp_phone_number || 'Not provided'}</div>
                            </div>
                          </div>
                        </div>

                        <div className="contact-item mb-3">
                          <div className="d-flex align-items-center">
                            <i className="ti ti-mail text-info me-3"></i>
                            <div>
                              <small className="text-muted">Email</small>
                              <div className="fw-medium">{userProfile.profileSettings.email || 'Not provided'}</div>
                            </div>
                          </div>
                        </div>

                        <div className="contact-item mb-3">
                          <div className="d-flex align-items-center">
                            <i className="ti ti-map-pin text-danger me-3"></i>
                            <div>
                              <small className="text-muted">Address</small>
                              <div className="fw-medium">
                                {(() => {
                                  const addressParts = [];
                                  if (userProfile.profileSettings.address_line_1) addressParts.push(userProfile.profileSettings.address_line_1);
                                  if (userProfile.profileSettings.address_line_2) addressParts.push(userProfile.profileSettings.address_line_2);
                                  if (userProfile.profileSettings.street) addressParts.push(userProfile.profileSettings.street);
                                  if (userProfile.profileSettings.city) addressParts.push(userProfile.profileSettings.city);
                                  if (userProfile.profileSettings.state) addressParts.push(userProfile.profileSettings.state);
                                  if (userProfile.profileSettings.country) addressParts.push(userProfile.profileSettings.country);
                                  if (userProfile.profileSettings.zip_code) addressParts.push(userProfile.profileSettings.zip_code);
                                  
                                  return addressParts.length > 0 
                                    ? addressParts.join(', ') 
                                    : 'Not provided';
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Support Hours */}
                      <div className="support-hours mt-4 p-3 bg-light rounded">
                        <h6 className="mb-2">
                          <i className="ti ti-clock me-2 text-warning"></i>
                          Support Hours
                        </h6>
                        <div className="small text-muted">
                          <div>Monday - Friday: 9:00 AM - 6:00 PM</div>
                          <div>Saturday: 9:00 AM - 2:00 PM</div>
                          <div>Sunday: Closed</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <i className="ti ti-building display-1 text-muted mb-3"></i>
                      <h5 className="text-muted">No Institution Details</h5>
                      <p className="text-muted">Institution details are not configured yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side - Support Tickets */}
            <div className="col-lg-8 col-md-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="ti ti-ticket me-2"></i>
                    My Support Tickets
                  </h5>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowCreateTicket(true)}
                  >
                    <i className="ti ti-plus me-2"></i>
                    Create Ticket
                  </button>
                </div>
                <div className="card-body">
                  {error ? (
                    <div className="text-center py-4">
                      <i className="ti ti-alert-triangle display-1 text-danger mb-3"></i>
                      <h5 className="text-danger">Error Loading Tickets</h5>
                      <p className="text-muted">{error}</p>
                      <button className="btn btn-primary" onClick={fetchSupportTickets}>
                        <i className="ti ti-refresh me-2"></i>
                        Retry
                      </button>
                    </div>
                  ) : tickets.length > 0 ? (
                    <div className="tickets-list">
                      {tickets.map((ticket) => {
                        const statusBadge = getStatusBadge(ticket.status);
                        const priorityBadge = getPriorityBadge(ticket.priority);
                        
                        return (
                          <div key={ticket.id} className="ticket-item border rounded p-3 mb-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="mb-1">#{ticket.id} - {ticket.title}</h6>
                                <p className="text-dark fw-medium mb-0">Subject: {ticket.subject}</p>
                              </div>
                              <div className="text-end">
                                <span className={`badge ${statusBadge.class} me-2`}>
                                  {statusBadge.text}
                                </span>
                                <span className={`badge ${priorityBadge.class}`}>
                                  {priorityBadge.text}
                                </span>
                              </div>
                            </div>
                            
                            <p className="text-muted small mb-2">
                              {ticket.description.length > 150 
                                ? `${ticket.description.substring(0, 150)}...` 
                                : ticket.description}
                            </p>

                            {/* File Attachments */}
                            {ticket.files && ticket.files.length > 0 && (
                              <div className="mb-2">
                                <div className="d-flex align-items-center mb-1">
                                  <i className="ti ti-paperclip me-1 text-muted"></i>
                                  <small className="text-muted fw-medium">Attachments:</small>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                  {ticket.files.map((file) => (
                                    <div key={file.id} className="d-flex align-items-center bg-light rounded px-2 py-1">
                                      <i className="ti ti-file me-1 text-primary"></i>
                                      <a 
                                        href={file.fileUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-decoration-none small"
                                      >
                                        {file.fileName}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="small text-muted">
                                <i className="ti ti-calendar me-1"></i>
                                Created: {formatDate(ticket.createdOn)}
                              </div>
                              <div className="small text-muted">
                                {ticket.ticketConversations && ticket.ticketConversations.length > 0 && (
                                  <span className="me-3">
                                    <i className="ti ti-message-circle me-1"></i>
                                    {ticket.ticketConversations.length} conversations
                                  </span>
                                )}
                                {ticket.files && ticket.files.length > 0 && (
                                  <span>
                                    <i className="ti ti-paperclip me-1"></i>
                                    {ticket.files.length} files
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="ti ti-ticket-off display-1 text-muted mb-3"></i>
                      <h5 className="text-muted">No Support Tickets</h5>
                      <p className="text-muted">You haven't created any support tickets yet.</p>
                      <button 
                        className="btn btn-primary"
                        onClick={() => setShowCreateTicket(true)}
                      >
                        <i className="ti ti-plus me-2"></i>
                        Create Your First Ticket
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo/>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Create Ticket Modal */}
      <CreateTicketModal 
        show={showCreateTicket}
        onClose={() => setShowCreateTicket(false)}
        onTicketCreated={handleTicketCreated}
      />
    </>
  );
};

export default Support;
